import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Settings2 } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  ContentType,
  getDefaultConfig,
  getSectionDefinition,
  getSupportedContentTypes,
  SECTION_DEFINITIONS,
} from "./homepageConfig";

import {
  useCreateScreenSectionsMutation,
  useLazyGetScreenSectionsQuery,
  useUpdateScreenSectionsMutation,
} from "../../../redux/actions/Admin/ProductAdmin";

// ============================================================
// FORM
// ============================================================

type SectionForm = {
  sectionType: string;

  /**
   * ContentType is a section-level property.
   *
   * IMPORTANT:
   * This is NOT stored inside ConfigJson.
   */
  contentType: ContentType | "";

  sectionName: string;
  displayName: string;
  description: string;

  layoutType: string;

  maxItems: number;
  allowMultiple: boolean;

  recommendedWidth: number;
  recommendedHeight: number;
  aspectRatio: string;

  mobileRecommendedWidth: number;
  mobileRecommendedHeight: number;
  mobileAspectRatio: string;

  isVisible: boolean;
  clientType: string;

  config: Record<string, any>;
};

// ============================================================
// LAYOUT OPTIONS
// ============================================================

/**
 * Layouts are section-specific.
 *
 * Do NOT expose every BannerLayoutType to every section.
 */
const SECTION_LAYOUTS: Record<string, { value: string; label: string }[]> = {
  MainCarousel: [
    {
      value: "HomepageHero",
      label: "Homepage Hero",
    },
    {
      value: "Carousel",
      label: "Carousel",
    },
  ],

  PeekCarousel: [
    {
      value: "PeekCarousel",
      label: "Peek Carousel",
    },
  ],

  // ------------------------------------------------------------
  // Strip Banner
  //
  // Three supported sizes:
  //
  // StripSmall
  //     Compact strip
  //
  // StripMedium
  //     Medium promotional strip
  //
  // StripLarge
  //     Large promotional strip
  // ------------------------------------------------------------
  StripBanner: [
    {
      value: "StripSmall",
      label: "Strip Small",
    },
    {
      value: "StripMedium",
      label: "Strip Medium",
    },
    {
      value: "StripLarge",
      label: "Strip Large",
    },
  ],

  PetPromoBanner: [
    {
      value: "FullWidthScale",
      label: "Full Width",
    },
  ],

  MultiBanner: [
    {
      value: "MultiGrid",
      label: "Multi Grid",
    },
  ],

  PetBanner: [
    {
      value: "PetBanner",
      label: "Pet Banner",
    },
  ],

  PromoBanner: [
    {
      value: "FullWidthScale",
      label: "Full Width",
    },
  ],

  WellnessBanner: [
    {
      value: "FullWidthScale",
      label: "Full Width",
    },
  ],

  ConsultationBanner: [
    {
      value: "FullWidthScale",
      label: "Full Width",
    },
  ],

  PetBannerFurry: [
    {
      value: "FullWidthScale",
      label: "Full Width",
    },
  ],
};

/**
 * Get layouts supported by the selected section.
 *
 * If no explicit layout configuration exists:
 * - keep existing layout while editing
 * - otherwise no layout
 */
const getLayoutOptions = (
  sectionType: string,
): { value: string; label: string }[] => {
  return SECTION_LAYOUTS[sectionType] || [];
};

// ============================================================
// DEFAULT CONTENT TYPE
// ============================================================

const getDefaultContentType = (
  sectionType: string,
  screenName: string,
): ContentType | "" => {
  const definition = getSectionDefinition(sectionType);

  // Explicit section definition takes priority.
  if (definition?.itemType) {
    return definition.itemType as ContentType;
  }

  const supportedTypes = getSupportedContentTypes(sectionType, screenName);

  if (supportedTypes.length > 0) {
    return supportedTypes[0];
  }

  // Banner-based sections do not use ScreenSectionItems.
  // Their content is managed through the Banner APIs.
  switch (sectionType) {
    case "MainCarousel":
    case "StripBanner":
    case "PetPromoBanner":
    case "PetBanner":
    case "PromoBanner":
    case "WellnessBanner":
    case "ConsultationBanner":
    case "MultiBanner":
      return "Custom";

    default:
      return "";
  }
};

// ============================================================
// COMPONENT
// ============================================================

const SectionFormScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sectionId } = useParams();

  const state = location.state as
    | {
        screenId?: number;
        screenName?: string;
        section?: any;
      }
    | undefined;

  const screenId = state?.screenId ?? 1;

  const screenName = state?.screenName || "Products";

  // ============================================================
  // API
  // ============================================================

  const [getSections, { data: sections }] = useLazyGetScreenSectionsQuery();

  const [createSection, { isLoading: isCreating }] =
    useCreateScreenSectionsMutation();

  const [updateSection, { isLoading: isUpdating }] =
    useUpdateScreenSectionsMutation();

  const isEdit = Boolean(sectionId);

  // ============================================================
  // FORM STATE
  // ============================================================

  const [form, setForm] = useState<SectionForm>({
    sectionType: "",
    contentType: "",

    sectionName: "",
    displayName: "",
    description: "",

    layoutType: "",

    maxItems: 10,
    allowMultiple: false,

    recommendedWidth: 0,
    recommendedHeight: 0,
    aspectRatio: "",

    mobileRecommendedWidth: 0,
    mobileRecommendedHeight: 0,
    mobileAspectRatio: "",

    isVisible: true,
    clientType: "All",

    config: {},
  });

  // ============================================================
  // LOAD SECTIONS FOR EDIT
  // ============================================================

  useEffect(() => {
    if (isEdit && !state?.section) {
      getSections(screenId);
    }
  }, [isEdit, state?.section, screenId, getSections]);

  // ============================================================
  // LOAD SECTION DATA
  // ============================================================

  useEffect(() => {
    const section =
      state?.section || sections?.find((x: any) => x.id === Number(sectionId));

    if (!section) {
      return;
    }

    // ----------------------------------------------------------
    // CONFIG
    // ----------------------------------------------------------

    let config: Record<string, any> = {};

    try {
      config = section.configJson
        ? JSON.parse(section.configJson)
        : getDefaultConfig(section.sectionType);
    } catch {
      config = getDefaultConfig(section.sectionType);
    }

    // ----------------------------------------------------------
    // CONTENT TYPE
    // ----------------------------------------------------------

    let contentType: ContentType | "" = section.contentType || "";

    /**
     * Backward compatibility:
     *
     * Older sections may have ContentType inside ConfigJson.
     */
    if (!contentType) {
      contentType = config.ContentType || "";
    }

    if (!contentType) {
      contentType = getDefaultContentType(section.sectionType, screenName);
    }

    /**
     * IMPORTANT:
     *
     * Custom content does not use SourceType.
     *
     * If an old/incorrect section has:
     *
     * ContentType = Custom
     * SourceType = Dynamic
     *
     * we remove SourceType from the form configuration.
     */
    if (contentType === "Custom") {
      const cleanedConfig = {
        ...config,
      };

      delete cleanedConfig.SourceType;

      config = cleanedConfig;
    }

    // ----------------------------------------------------------
    // LAYOUT
    // ----------------------------------------------------------

    const layoutOptions = getLayoutOptions(section.sectionType);

    let layoutType = section.layoutType || "";

    /**
     * If there is only one valid layout,
     * use it automatically.
     */
    if (!layoutType && layoutOptions.length === 1) {
      layoutType = layoutOptions[0].value;
    }

    // ----------------------------------------------------------
    // FORM
    // ----------------------------------------------------------

    setForm({
      sectionType: section.sectionType || "",

      contentType,

      sectionName: section.sectionName || "",

      displayName: section.displayName || "",

      description: section.description || "",

      layoutType,

      maxItems: section.maxItems ?? 10,

      allowMultiple: section.allowMultiple ?? false,

      recommendedWidth: section.recommendedWidth ?? 0,

      recommendedHeight: section.recommendedHeight ?? 0,

      aspectRatio: section.aspectRatio || "",

      mobileRecommendedWidth: section.mobileRecommendedWidth ?? 0,

      mobileRecommendedHeight: section.mobileRecommendedHeight ?? 0,

      mobileAspectRatio: section.mobileAspectRatio || "",

      isVisible: section.isVisible ?? true,

      clientType: section.clientType || "All",

      config,
    });
  }, [state?.section, sections, sectionId, screenName]);

  // ============================================================
  // SECTION DEFINITIONS
  // ============================================================

  const definitions = useMemo(
    () => SECTION_DEFINITIONS[screenName] || [],
    [screenName],
  );

  // ============================================================
  // SELECTED SECTION
  // ============================================================

  const selectedDefinition = getSectionDefinition(form.sectionType, screenName);

  // ============================================================
  // SUPPORTED CONTENT TYPES
  // ============================================================

  const supportedContentTypes = useMemo(
    () => getSupportedContentTypes(form.sectionType, screenName),
    [form.sectionType, screenName],
  );

  // ============================================================
  // LAYOUT OPTIONS
  // ============================================================

  const layoutOptions = useMemo(
    () => getLayoutOptions(form.sectionType),
    [form.sectionType],
  );

  // ============================================================
  // CONTENT TYPE LABEL
  // ============================================================

  const getContentTypeLabel = (contentType: ContentType): string => {
    switch (contentType) {
      case "Product":
        return "Products";

      case "Category":
        return "Categories";

      case "Brand":
        return "Brands";

      case "Pet":
        return "Pets";

      case "Service":
        return "Services";

      case "Video":
        return "Video";

      case "Custom":
        return "Custom Content";

      default:
        return contentType;
    }
  };

  // ============================================================
  // CHANGE SECTION TYPE
  // ============================================================

  const changeSectionType = (value: string) => {
    const existing = getSectionDefinition(value, screenName);

    const defaultConfig = getDefaultConfig(value);

    const newLayoutOptions = getLayoutOptions(value);

    const contentType = getDefaultContentType(value, screenName);

    let layoutType = "";

    if (newLayoutOptions.length === 1) {
      layoutType = newLayoutOptions[0].value;
    }

    /**
     * Custom content does not use
     * Dynamic/Manual SourceType.
     */
    const cleanedConfig = {
      ...defaultConfig,
    };

    if (contentType === "Custom") {
      delete cleanedConfig.SourceType;
    }

    setForm((prev) => ({
      ...prev,

      sectionType: value,

      contentType,

      sectionName: prev.sectionName || existing?.label || "",

      displayName: prev.displayName || existing?.label || "",

      layoutType,

      config: cleanedConfig,
    }));
  };

  // ============================================================
  // CHANGE CONTENT TYPE
  // ============================================================

  const changeContentType = (value: ContentType) => {
    /**
     * ContentType is a section-level property.
     *
     * It is intentionally NOT stored in ConfigJson.
     *
     * IMPORTANT:
     *
     * Custom content means:
     * - Admin manages custom content
     * - Manage Content is used
     * - SourceType Dynamic/Manual is not applicable
     */
    setForm((prev) => {
      const nextConfig = {
        ...prev.config,
      };

      if (value === "Custom") {
        delete nextConfig.SourceType;
      }

      return {
        ...prev,
        contentType: value,
        config: nextConfig,
      };
    });
  };

  // ============================================================
  // UPDATE NORMAL FIELD
  // ============================================================

  const updateField = (field: keyof SectionForm, value: any) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ============================================================
  // UPDATE CONFIG
  // ============================================================

  const updateConfig = (key: string, value: any) => {
    setForm((prev) => ({
      ...prev,

      config: {
        ...prev.config,
        [key]: value,
      },
    }));
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ----------------------------------------------------------
    // Validation
    // ----------------------------------------------------------

    if (!form.sectionType) {
      alert("Please select a section type.");
      return;
    }

    if (!form.contentType) {
      alert("Please select a content type.");
      return;
    }

    if (!form.sectionName.trim()) {
      alert("Please enter an internal section name.");
      return;
    }

    if (!form.displayName.trim()) {
      alert("Please enter a display name.");
      return;
    }

    // ----------------------------------------------------------
    // Validate layout
    // ----------------------------------------------------------

    if (layoutOptions.length > 0 && !form.layoutType) {
      alert("Please select a layout.");
      return;
    }

    // ----------------------------------------------------------
    // CLEAN CONFIG
    // ----------------------------------------------------------

    /**
     * Clone configuration before submitting.
     *
     * Custom content MUST NOT contain SourceType.
     */
    const finalConfig = {
      ...form.config,
    };

    if (form.contentType === "Custom") {
      delete finalConfig.SourceType;
    }

    // ----------------------------------------------------------
    // PAYLOAD
    // ----------------------------------------------------------

    const payload = {
      sectionType: form.sectionType,

      contentType: form.contentType,

      sectionName: form.sectionName.trim(),

      displayName: form.displayName.trim(),

      description: form.description.trim(),

      layoutType: form.layoutType || null,

      maxItems: form.maxItems,

      allowMultiple: form.allowMultiple,

      recommendedWidth: form.recommendedWidth,

      recommendedHeight: form.recommendedHeight,

      aspectRatio: form.aspectRatio || null,

      mobileAspectRatio: form.mobileAspectRatio || null,

      mobileRecommendedWidth: form.mobileRecommendedWidth || 0,

      mobileRecommendedHeight: form.mobileRecommendedHeight || 0,

      isVisible: form.isVisible,

      clientType: form.clientType,

      ConfigJson:
        Object.keys(finalConfig).length > 0 ? JSON.stringify(finalConfig) : "",
    };

    try {
      // --------------------------------------------------------
      // UPDATE
      // --------------------------------------------------------

      if (isEdit) {
        await updateSection({
          id: Number(sectionId),
          data: payload,
        }).unwrap();
      }

      // --------------------------------------------------------
      // CREATE
      // --------------------------------------------------------
      else {
        await createSection({
          id: screenId,
          data: payload,
        }).unwrap();
      }

      // --------------------------------------------------------
      // BACK TO SECTIONS
      // --------------------------------------------------------

      navigate("/admin/uiconfigure/sections", {
        state: {
          screenId,
          screenName,
        },
      });
    } catch (error) {
      console.error("Section save failed", error);

      alert("Failed to save section.");
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-12">
      {/* ======================================================
          BACK
      ====================================================== */}

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-slate-500 mb-6 hover:text-slate-700"
      >
        <ArrowLeft size={17} />
        Back to Sections
      </button>

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mb-8">
        <h1 className="text-2xl font-semibold">
          {isEdit ? "Edit Section" : "Add Section"}
        </h1>

        <p className="text-sm text-slate-500 mt-1">
          {isEdit
            ? "Update the section configuration. Content type cannot be changed after creation."
            : "Configure the section structure. Content items are managed separately."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ====================================================
            SECTION INFORMATION
        ==================================================== */}

        <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
          {/* ==================================================
              SECTION TYPE
          ================================================== */}

          <div>
            <label className="block text-sm font-medium mb-2">
              Section Type
            </label>

            <select
              value={form.sectionType}
              onChange={(e) => changeSectionType(e.target.value)}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white"
              required
              disabled={isEdit}
            >
              <option value="">Select section</option>

              {definitions
                .filter((definition) => {
                  // ---------------------------------------------------------
                  // CREATE
                  // Only configurable sections can be added.
                  // ---------------------------------------------------------
                  if (!isEdit) {
                    return definition.configurable === true;
                  }

                  // ---------------------------------------------------------
                  // EDIT
                  // Keep the existing section available even if it is
                  // non-configurable.
                  // ---------------------------------------------------------
                  return (
                    definition.configurable === true ||
                    definition.value === form.sectionType
                  );
                })
                .map((definition) => (
                  <option key={definition.value} value={definition.value}>
                    {definition.label}
                  </option>
                ))}
            </select>

            {isEdit && (
              <p className="text-xs text-slate-400 mt-2">
                Section type cannot be changed after creation.
              </p>
            )}
          </div>

          {/* ==================================================
              CONTENT TYPE
          ================================================== */}

          {form.sectionType && form.contentType && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Content Type
              </label>

              {supportedContentTypes.length > 1 && !isEdit ? (
                <select
                  value={form.contentType}
                  onChange={(e) =>
                    changeContentType(e.target.value as ContentType)
                  }
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white"
                >
                  <option value="">Select content type</option>

                  {supportedContentTypes.map((contentType) => (
                    <option key={contentType} value={contentType}>
                      {getContentTypeLabel(contentType)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-sm">
                  {getContentTypeLabel(form.contentType)}
                </div>
              )}

              <p className="text-xs text-slate-400 mt-2">
                {form.contentType === "Custom"
                  ? "Custom content is managed separately from the section configuration."
                  : supportedContentTypes.length > 1
                    ? "Choose the type of content this section will manage."
                    : `This section uses ${getContentTypeLabel(
                        form.contentType,
                      )} content.`}
              </p>

              {/* ==================================================
                    CUSTOM CONTENT INFORMATION
                ================================================== */}

              {form.contentType === "Custom" && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-xs font-semibold text-amber-800">
                    Custom Content
                  </p>

                  <p className="text-xs text-amber-700 mt-1">
                    This section uses custom content managed by the
                    administrator. Images, titles and other content can be added
                    from the Manage Content screen.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ==================================================
              SECTION INFO
          ================================================== */}

          {selectedDefinition && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <Settings2 size={17} />

                <span className="text-sm font-medium">
                  {selectedDefinition.label}
                </span>
              </div>

              <p className="text-xs text-slate-500 mt-2">
                {isEdit
                  ? "Section structure is fixed. You can update display settings and manage its content separately."
                  : "Select the content type and configure how this section will appear."}
              </p>
            </div>
          )}

          {/* ==================================================
              INTERNAL NAME
          ================================================== */}

          <div>
            <label className="block text-sm font-medium mb-2">
              Internal Section Name
            </label>

            <input
              value={form.sectionName}
              onChange={(e) => updateField("sectionName", e.target.value)}
              placeholder="Example: best-selling-products"
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>

          {/* ==================================================
              DISPLAY NAME
          ================================================== */}

          <div>
            <label className="block text-sm font-medium mb-2">
              Display Name
            </label>

            <input
              value={form.displayName}
              onChange={(e) => updateField("displayName", e.target.value)}
              placeholder="Example: Best Selling Products"
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm"
              required
            />
          </div>

          {/* ==================================================
              DESCRIPTION
          ================================================== */}

          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>

            <textarea
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              rows={3}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm resize-none"
              placeholder="Optional description for administrators."
            />
          </div>
        </div>

        {/* ====================================================
            LAYOUT & DISPLAY
        ==================================================== */}

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-lg font-medium mb-2">Layout & Display</h2>

          <p className="text-xs text-slate-500 mb-5">
            Configure how this section is displayed on the selected client.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* ==================================================
                LAYOUT
            ================================================== */}

            {layoutOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">Layout</label>

                {layoutOptions.length === 1 ? (
                  <>
                    <div className="w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-sm">
                      {layoutOptions[0].label}
                    </div>

                    <p className="text-xs text-slate-400 mt-2">
                      This section uses the {layoutOptions[0].label} layout.
                    </p>
                  </>
                ) : (
                  <>
                    <select
                      value={form.layoutType}
                      onChange={(e) =>
                        updateField("layoutType", e.target.value)
                      }
                      className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white"
                    >
                      <option value="">Select layout</option>

                      {layoutOptions.map((layout) => (
                        <option key={layout.value} value={layout.value}>
                          {layout.label}
                        </option>
                      ))}
                    </select>

                    <p className="text-xs text-slate-400 mt-2">
                      Only layouts supported by this section are available.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ==================================================
                CLIENT TYPE
            ================================================== */}

            <div>
              <label className="block text-sm font-medium mb-2">
                Client Type
              </label>

              <select
                value={form.clientType}
                onChange={(e) => updateField("clientType", e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white"
              >
                <option value="All">All</option>

                <option value="Web">Web</option>

                <option value="Mobile">Mobile</option>
              </select>
            </div>

            {/* ==================================================
                MAX ITEMS
            ================================================== */}

            <div>
              <label className="block text-sm font-medium mb-2">
                Maximum Items
              </label>

              <input
                type="number"
                min={1}
                value={form.maxItems}
                onChange={(e) =>
                  updateField("maxItems", Number(e.target.value))
                }
                className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm"
              />
            </div>

            {/* ==================================================
                ALLOW MULTIPLE
            ================================================== */}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.allowMultiple}
                onChange={(e) => updateField("allowMultiple", e.target.checked)}
                className="h-4 w-4"
              />

              <label className="text-sm">Allow multiple</label>
            </div>

            {/* ==================================================
                VISIBLE
            ================================================== */}

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.isVisible}
                onChange={(e) => updateField("isVisible", e.target.checked)}
                className="h-4 w-4"
              />

              <label className="text-sm">Visible</label>
            </div>
          </div>
        </div>

        {/* ====================================================
            SECTION CONFIGURATION
        ==================================================== */}

        {selectedDefinition?.configurable && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="text-lg font-medium mb-2">Section Configuration</h2>

            <p className="text-xs text-slate-500 mb-5">
              {form.contentType === "Custom"
                ? "Configure the section itself. Custom content is managed separately from this screen."
                : "These settings control how the section gets its data."}
            </p>

            {/* ==================================================
                ITEM COUNT
            ================================================== */}

            {"ItemCount" in form.config && (
              <div className="mb-5">
                <label className="block text-sm font-medium mb-2">
                  Item Count
                </label>

                <input
                  type="number"
                  min={1}
                  value={form.config.ItemCount ?? 10}
                  onChange={(e) =>
                    updateConfig("ItemCount", Number(e.target.value))
                  }
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm"
                />
              </div>
            )}

            {/* ==================================================
                DATA SOURCE
            ================================================== */}

            {form.contentType !== "Custom" && "SourceType" in form.config && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Data Source
                </label>

                <select
                  value={form.config.SourceType || "Dynamic"}
                  onChange={(e) => updateConfig("SourceType", e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white"
                >
                  <option value="Dynamic">Dynamic</option>

                  <option value="Manual">Manual</option>
                </select>

                <p className="text-xs text-slate-400 mt-2">
                  Dynamic content is loaded automatically from the system.
                  Manual content is managed from the Manage Content screen.
                </p>
              </div>
            )}

            {/* ==================================================
                ONLY IN STOCK
            ================================================== */}

            {"OnlyInStock" in form.config && (
              <label className="flex items-center gap-3 mt-5 text-sm">
                <input
                  type="checkbox"
                  checked={form.config.OnlyInStock ?? true}
                  onChange={(e) =>
                    updateConfig("OnlyInStock", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                Only show in-stock items
              </label>
            )}

            {/* ==================================================
                SHOW OFFER
            ================================================== */}

            {"ShowOffer" in form.config && (
              <label className="flex items-center gap-3 mt-4 text-sm">
                <input
                  type="checkbox"
                  checked={form.config.ShowOffer ?? false}
                  onChange={(e) => updateConfig("ShowOffer", e.target.checked)}
                  className="h-4 w-4"
                />
                Show offers
              </label>
            )}

            {/* ==================================================
                TRENDING ONLY
            ================================================== */}

            {"TrendingOnly" in form.config && (
              <label className="flex items-center gap-3 mt-4 text-sm">
                <input
                  type="checkbox"
                  checked={form.config.TrendingOnly ?? false}
                  onChange={(e) =>
                    updateConfig("TrendingOnly", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                Trending only
              </label>
            )}
          </div>
        )}

        {/* ====================================================
            ACTIONS
        ==================================================== */}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="border border-slate-300 rounded-xl px-5 py-3 text-sm hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isCreating || isUpdating}
            className="bg-black text-white rounded-xl px-6 py-3 text-sm disabled:opacity-50"
          >
            {isCreating || isUpdating
              ? "Saving..."
              : isEdit
                ? "Update Section"
                : "Create Section"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SectionFormScreen;
